import { TechnologySelectDto, UserProfileRole } from '@@types/api.types';
import { useTechnologiesQueryOptions, useTechnologyQueryOptions } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { useQuery } from '@tanstack/react-query';
import { type SankeyChartConfig, SankeyDataBuilder, createSankeyChartUtils } from '@utils/sankey.util';
import { mapCountryCodeToCountry } from '@utils/text.util';
import { ZoomInIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SankeyModal } from './sankey-modal.component';
import { Sankey, SankeyData, SankeyProps, SankeySettings } from './sankey.component';
import { Tile } from './tile.component';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Node types for the technology origins flow
 */
type NodeType = 'component' | 'origin' | 'subsystem' | 'technology';

/**
 * Context data for technology origins nodes
 */
type NodeContext = {
  technologyId?: string;
  type: NodeType;
};

/**
 * Props for the Technology Origins Sankey component
 */
export type TechnologyOriginsSankeyProps = {
  technologies: TechnologySelectDto[];
  technologyId?: string;
  sankeyProps?: Partial<SankeyProps>;
  settings?: Partial<SankeySettings>;
};

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Prefix for synthetic component nodes when subsystems have origins but no components with origins
 */
const NO_COMPONENT_PREFIX = '__NO_COMPONENT__';

/**
 * Configuration for Technology Origins Sankey chart
 * Defines colors, labels, and custom formatting for each node type
 */
const TECHNOLOGY_ORIGINS_CONFIG: SankeyChartConfig<NodeType> = {
  nodeTypes: {
    component: {
      color: '#EC4899',
      // Hide uniqueness suffix for synthetic no-component nodes
      formatLabel: (name: string) => (name.startsWith(NO_COMPONENT_PREFIX) ? '(No Component)' : name),
      label: 'Component',
    },
    origin: {
      color: '#F59E0B',
      formatLabel: (name: string) => mapCountryCodeToCountry(name) ?? name,
      label: 'Origin',
    },
    subsystem: {
      color: '#F43F5E',
      label: 'Subsystem',
    },
    technology: {
      color: '#818CF8',
      label: 'Technology',
    },
  },
};

// Create chart utilities using the configuration
const chartUtils = createSankeyChartUtils(TECHNOLOGY_ORIGINS_CONFIG);

// =============================================================================
// DATA PROCESSING
// =============================================================================

/**
 * Processes technology data to create Sankey-compatible data structure
 *
 * Creates a flow from Technology -> Subsystem -> Component -> Origin (Country)
 */
function processTechnologyOriginsData(
  technologies: TechnologySelectDto[],
  technologyId?: string,
): SankeyData<Record<string, unknown>, NodeContext> {
  const builder = new SankeyDataBuilder<NodeContext>();

  // Filter technologies if specific ID provided
  const filteredTechnologies = technologies.filter((tech) => !technologyId || tech.id === technologyId);

  // Process each technology
  for (const technology of filteredTechnologies) {
    // Skip technologies without subsystems or components with origins
    const hasValidData = technology.subsystems?.some(
      (subsystem) => subsystem.origin || subsystem.components?.some((component) => component.origin),
    );

    if (!hasValidData) continue;

    // Create technology node
    builder.addNode({
      name: technology.name,
      type: 'technology',
      extraData: {
        technologyId: technology.id,
        type: 'technology',
      },
    });

    // Process subsystems
    for (const subsystem of technology.subsystems || []) {
      // Only include subsystems with components that have origins
      const componentsWithOrigins = subsystem.components?.filter((comp) => comp.origin) || [];
      const hasSubsystemOrigin = Boolean(subsystem.origin);
      if (componentsWithOrigins.length === 0 && !hasSubsystemOrigin) continue;

      // Create subsystem node
      builder.addNode({
        name: subsystem.name,
        type: 'subsystem',
        extraData: {
          technologyId: technology.id,
          type: 'subsystem',
        },
      });

      // Add technology -> subsystem link
      builder.addLink({
        sourceName: technology.name,
        sourceType: 'technology',
        targetName: subsystem.name,
        targetType: 'subsystem',
        value: componentsWithOrigins.length > 0 ? componentsWithOrigins.length : 1,
      });

      // Process components with origins
      for (const component of componentsWithOrigins) {
        // Create component node
        builder.addNode({
          name: component.name,
          type: 'component',
          extraData: {
            technologyId: technology.id,
            type: 'component',
          },
        });

        // Add subsystem -> component link
        builder.addLink({
          sourceName: subsystem.name,
          sourceType: 'subsystem',
          targetName: component.name,
          targetType: 'component',
          value: 1,
        });

        // Process origin
        if (component.origin) {
          // Create origin node
          builder.addNode({
            name: component.origin,
            type: 'origin',
            extraData: {
              type: 'origin',
            },
          });

          // Add component -> origin link
          builder.addLink({
            sourceName: component.name,
            sourceType: 'component',
            targetName: component.origin,
            targetType: 'origin',
            value: 1,
          });
        }
      }

      // If no components have origins but the subsystem has an origin, add a synthetic component node
      if (componentsWithOrigins.length === 0 && hasSubsystemOrigin && subsystem.origin) {
        const syntheticComponentName = `${NO_COMPONENT_PREFIX}::${technology.id}::${subsystem.name}`;

        // Create synthetic component node
        builder.addNode({
          name: syntheticComponentName,
          type: 'component',
          extraData: {
            technologyId: technology.id,
            type: 'component',
          },
        });

        // Link subsystem -> synthetic component
        builder.addLink({
          sourceName: subsystem.name,
          sourceType: 'subsystem',
          targetName: syntheticComponentName,
          targetType: 'component',
          value: 1,
        });

        // Create origin node
        builder.addNode({
          name: subsystem.origin,
          type: 'origin',
          extraData: {
            type: 'origin',
          },
        });

        // Link synthetic component -> origin
        builder.addLink({
          sourceName: syntheticComponentName,
          sourceType: 'component',
          targetName: subsystem.origin,
          targetType: 'origin',
          value: 1,
        });
      }
    }
  }

  return builder.getData();
}

function technologyHasOrigins(technology: TechnologySelectDto): boolean {
  return (
    technology.subsystems?.some(
      (subsystem) => subsystem.origin || subsystem.components?.some((component) => component.origin),
    ) ?? false
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Technology Origins Sankey Chart
 *
 * A simple component that visualizes the flow from technologies through
 * subsystems and components to their origins (countries).
 *
 * The component focuses on data processing and formatting, delegating
 * the actual chart rendering to the basic Sankey component.
 *
 * Flow: Technology -> Subsystem -> Component -> Origin (Country)
 */
const TechnologyOriginsSankeyChart: React.FC<TechnologyOriginsSankeyProps> = (props) => {
  const { technologies, technologyId, sankeyProps, settings } = props;
  const navigate = useNavigate();

  // Process the technology data into Sankey format
  const sankeyData = useMemo(
    () => processTechnologyOriginsData(technologies, technologyId),
    [technologies, technologyId],
  );

  const handleNodeClick = (node: NodeContext & { name: string }, nodeIndex: number) => {
    if (node.technologyId && (node.type === 'component' || node.type === 'subsystem' || node.type === 'technology')) {
      navigate(`/technology/${node.technologyId}`);
    }
  };

  if (!sankeyData.nodes.length || !sankeyData.links.length) {
    console.error('No data to display in Technology Origins Sankey');
    return null;
  }

  return (
    <div className="w-full">
      <Sankey
        data={sankeyData}
        formatNodeLabel={chartUtils.formatNodeLabel}
        formatNodeSubtext={chartUtils.formatNodeSubtext}
        onNodeClick={handleNodeClick}
        settings={{
          colorMap: chartUtils.getColorMap(),
          height: 'auto',
          ...settings,
        }}
        {...sankeyProps}
      />
    </div>
  );
};

export const TechnologyOriginsSankey = React.memo(TechnologyOriginsSankeyChart);

type TechnologyOriginsSankeyTileProps = {
  technologyId?: string;
  sankeyProps?: Partial<SankeyProps>;
  settings?: Partial<SankeySettings>;
};

export const TechnologyOriginsSankeyTile = (props: TechnologyOriginsSankeyTileProps) => {
  const { technologyId, settings, sankeyProps } = props;
  const technologiesQueryOptions = useTechnologiesQueryOptions();
  const technologyQueryOptions = useTechnologyQueryOptions(technologyId);
  const multipleTechnologiesQuery = useQuery({
    ...technologiesQueryOptions,
    enabled: !technologyId && technologiesQueryOptions.enabled,
  });
  const singleTechnologyQuery = useQuery({
    ...technologyQueryOptions,
    enabled: Boolean(technologyId) && technologyQueryOptions.enabled,
  });
  const { data: user } = useGetCurrentUser();
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;

  const technologiesWithOrigins = useMemo(() => {
    if (technologyId) {
      const singleTechnology = singleTechnologyQuery.data;
      return singleTechnology && technologyHasOrigins(singleTechnology) ? [singleTechnology] : [];
    }

    return (multipleTechnologiesQuery.data ?? []).filter(technologyHasOrigins);
  }, [multipleTechnologiesQuery.data, singleTechnologyQuery.data, technologyId]);

  const [isOpenFullScreenModal, setIsOpenFullScreenModal] = useState(false);

  // We don't need to show the technology options if we are viewing a single technology
  const technologyOptions = technologyId ? undefined : technologiesWithOrigins.map((t) => ({ id: t.id, name: t.name }));

  function handleOpenFullScreen() {
    setIsOpenFullScreenModal(true);
  }

  function handleCloseFullScreen() {
    setIsOpenFullScreenModal(false);
  }

  const isEmpty = technologiesWithOrigins.length === 0;

  return (
    <>
      <Tile
        header={{
          title: 'Technology Origins',
          iconButton: {
            icon: <ZoomInIcon />,
            onClick: handleOpenFullScreen,
          },
        }}
        emptyState={{
          title: 'No Technology Origins',
          message: isInnovator ? 'Add Components to your Technology Subsystems to visualize their Origins' : undefined,
        }}
        hoverReveal
        isEmpty={isEmpty}
      >
        <TechnologyOriginsSankey
          technologies={technologiesWithOrigins || []}
          technologyId={technologyId}
          settings={settings}
          sankeyProps={{
            ...sankeyProps,
            onOpenFullScreen: handleOpenFullScreen,
          }}
        />
      </Tile>

      {isOpenFullScreenModal && (
        <SankeyModal
          isOpen={isOpenFullScreenModal}
          onClose={handleCloseFullScreen}
          title="Technology Origins"
          technologyOptions={technologyOptions}
        >
          {({ settings, selectedTechnologyId }) => (
            <TechnologyOriginsSankey
              technologies={technologiesWithOrigins || []}
              technologyId={selectedTechnologyId}
              settings={settings}
            />
          )}
        </SankeyModal>
      )}
    </>
  );
};
